import { XCircle, FileText } from 'lucide-react'
import { Button } from '@/components/ui/button'

interface SellerDetailsModalProps {
  store: any
  onClose: () => void
  onAction: (action: 'approve' | 'reject' | 'block' | 'unblock') => void
}

export function SellerDetailsModal({ store, onClose, onAction }: SellerDetailsModalProps) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="p-4 sm:p-6">
          <div className="flex justify-between items-start">
            <h3 className="text-xl font-bold text-gray-900">
              Détails de la Demande Vendeur
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <XCircle className="h-6 w-6" />
            </button>
          </div>

          <div className="mt-6 space-y-6">
            {/* Informations Personnelles */}
            <div>
              <h4 className="text-lg font-medium text-gray-900 mb-4">
                Informations Personnelles
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-500">Nom Complet</label>
                  <p className="mt-1 text-sm text-gray-900">{store.applicant_name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Email</label>
                  <p className="mt-1 text-sm text-gray-900">{store.applicant_email}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Téléphone</label>
                  <p className="mt-1 text-sm text-gray-900">{store.applicant_phone}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Adresse</label>
                  <p className="mt-1 text-sm text-gray-900">{store.address}</p>
                </div>
              </div>
            </div>

            {/* Informations de la Boutique */}
            <div>
              <h4 className="text-lg font-medium text-gray-900 mb-4">
                Informations de la Boutique
              </h4>
              <div className="grid grid-cols-1 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-500">Nom de la Boutique</label>
                  <p className="mt-1 text-sm text-gray-900">{store.name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Description</label>
                  <p className="mt-1 text-sm text-gray-900">{store.description}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Catégories</label>
                  <div className="mt-1 flex flex-wrap gap-2">
                    {store.categories?.map((category: string) => (
                      <span
                        key={category}
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                      >
                        {category}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Type d'Abonnement</label>
                  <p className="mt-1 text-sm text-gray-900 capitalize">{store.subscription_type}</p>
                </div>
              </div>
            </div>

            {/* Documents */}
            <div>
              <h4 className="text-lg font-medium text-gray-900 mb-4">
                Documents Fournis
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {store.document_urls && Object.entries(store.document_urls).map(([key, url]) => (
                  url && (
                    <div key={key} className="relative">
                      <label className="block text-sm font-medium text-gray-500 mb-2 capitalize">
                        {key.replace(/_/g, ' ')}
                      </label>
                      <img
                        src={url as string}
                        alt={key}
                        className="w-full h-48 object-cover rounded-lg border border-gray-200"
                      />
                      <a
                        href={url as string}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="absolute top-8 right-2 p-2 bg-white rounded-full shadow-lg hover:bg-gray-50"
                      >
                        <FileText className="h-4 w-4 text-gray-500" />
                      </a>
                    </div>
                  )
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="pt-6 border-t flex justify-end space-x-3">
              <Button
                variant="secondary"
                onClick={onClose}
              >
                Fermer
              </Button>
              {store.status === 'pending' && (
                <>
                  <Button
                    variant="outline"
                    className="text-red-600 hover:text-red-700"
                    onClick={() => onAction('reject')}
                  >
                    Rejeter
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => onAction('approve')}
                  >
                    Approuver
                  </Button>
                </>
              )}
              {store.status === 'approved' && (
                <Button
                  variant="outline"
                  className="text-red-600 hover:text-red-700"
                  onClick={() => onAction('block')}
                >
                  Bloquer
                </Button>
              )}
              {store.status === 'suspended' && (
                <Button
                  variant="primary"
                  onClick={() => onAction('unblock')}
                >
                  Débloquer
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}